import { Component, computed, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

import { of, iif, combineLatest, map, tap, switchMap } from 'rxjs';

import {
  CampaignTagToLabelKey,
  CmsDataService,
  CMSDiscountCode,
  CurrencyService,
  isPresent,
  LocationRouteCffService,
} from '@fcom/core';
import { CmsContentWithDestination, CmsImageData } from '@fcom/core-api';
import { GtmService } from '@fcom/common/gtm/services/gtm.service';
import { LanguageService } from '@fcom/ui-translate';
import { AmDestinationService } from '@fcom/destination-search/services/am-destination.service';
import { ContentType } from '@fcom/common/interfaces/gtm.interface';
import { getDestinationInCampaign } from '@fcom/core/utils/cms-data-utils';
import { ReplaceParams } from '@fcom/core/utils/replaceMsgFormat';

interface HeroConfig {
  title: string;
  backgroundImage: CmsImageData;
  destinationCode: string;
  enableAward: boolean;
  enablePromoCode: boolean;
  enableAurinkomatkat: boolean;
  contentType: ContentType;
  isInCampaign: boolean;
  campaignLabel: string;
  campaignEndText: string;
  campaignBlackTeaser: boolean;
  discountCode: CMSDiscountCode;
}

/**
 * CMS - Hero component for destination page
 *
 * @example
 * <cms-destination-hero [content]="content" />
 */
@Component({
  selector: 'cms-destination-hero',
  templateUrl: './cms-destination-hero.component.html',
  standalone: false,
})
export class CmsDestinationHeroComponent {
  private gtmService = inject(GtmService);
  private amDestinationService = inject(AmDestinationService);
  private locationRouteCffService = inject(LocationRouteCffService);
  private languageService = inject(LanguageService);
  private cmsDataService = inject(CmsDataService);
  private currencyService = inject(CurrencyService);

  content = input.required<CmsContentWithDestination>();

  readonly heroConfig = toSignal<HeroConfig>(
    combineLatest([
      toObservable(this.content),
      this.cmsDataService.getOngoingCampaigns(this.languageService.langValue, this.languageService.localeValue),
    ]).pipe(
      tap(([content]) => {
        this.gtmService.destinationPromotion(
          'destination-hero-banner',
          content.destination.defaultLocation.locationCode,
          content.destination.defaultLocation.geoLocation?.countryCode
        );
      }),
      map(([content, campaigns]) => {
        const locationInCampaign = getDestinationInCampaign(
          campaigns,
          content.destination.defaultLocation.locationCode
        );

        return {
          title: content.destination.title,
          backgroundImage: content.destination.picture,
          destinationCode: content.destination.defaultLocation.locationCode,
          enableAward: this.getConfigProperty<boolean>(content.localSettings, 'enableAward', true),
          enablePromoCode: this.getConfigProperty<boolean>(content.localSettings, 'enablePromoCode', true),
          enableAurinkomatkat: false,
          contentType: ContentType.SINGLE_DESTINATION,
          isInCampaign: campaigns.some(
            (campaign) =>
              campaign?.destinations?.some(
                (dest: string) => dest === content.destination.defaultLocation.locationCode
              ) ||
              Object.values(campaign?.destinationsByOrigin || {})
                .flat()
                .some((dest: string) => dest === content.destination.defaultLocation.locationCode)
          ),
          campaignLabel: locationInCampaign?.campaignLabel,
          campaignEndText: locationInCampaign?.campaignEndText,
          campaignBlackTeaser: locationInCampaign?.enableBlackTeaser,
          discountCode: locationInCampaign?.discountCode,
        };
      }),
      switchMap((config) =>
        iif(
          //only fetch the amlist and locale city name when it is FI site
          () => this.languageService.localeValue.includes('FI'),
          this.amDestinationService.amDestinationList().pipe(
            // first check if the am destination list includes the destinationCode,
            // if not in the list return the list for further check
            map((amList) => (amList.map(({ dest }) => dest).includes(config.destinationCode) ? null : amList)),
            switchMap((amList) =>
              iif(
                () => !!amList,
                // if the destinationCode is not find in am destination list, then check if the destination name match
                this.locationRouteCffService.getLocaleCityName(config.destinationCode, 'fi_FI').pipe(
                  map((localeCityName) => ({
                    ...config,
                    // enable the aurinkomatkat when the aurinko matka service is avalible and it is FI site
                    enableAurinkomatkat: amList.map(({ destName_fi }) => destName_fi).includes(localeCityName),
                  }))
                ),
                of({ ...config, enableAurinkomatkat: true })
              )
            )
          ),
          of(config)
        )
      )
    )
  );

  readonly campaignLabelKey = computed(() => {
    const config = this.heroConfig();
    if (!config || !isPresent(config.isInCampaign) || !isPresent(config.campaignLabel)) {
      return undefined;
    }

    const { campaignLabel, discountCode } = config;
    if (campaignLabel === CampaignTagToLabelKey.fcomCampaignCode && isPresent(discountCode?.type)) {
      if (discountCode.type === 'fixed') {
        return 'destinationSearch.discountCodeFixed';
      }
      return 'destinationSearch.discountCodePercentage';
    }

    return `destinationSearch.${campaignLabel}`;
  });

  private readonly currencyCode = this.languageService.translateSignal('number.currency');

  private readonly discountCodeFixedAmount = computed(() => {
    const discountCode = this.heroConfig()?.discountCode;
    if (discountCode?.type !== 'fixed') {
      return undefined;
    }

    return this.currencyService.formatPriceValue(
      {
        amount: discountCode.amount,
        currencyCode: this.currencyCode(),
      },
      { stripZeroCents: true, useSymbol: true }
    );
  });

  readonly campaignLabelParams = computed<ReplaceParams>(() => {
    const discountCode = this.heroConfig()?.discountCode;
    if (!discountCode) {
      return undefined;
    }

    if (discountCode.type === 'fixed') {
      return { amount: this.discountCodeFixedAmount() };
    }

    return { amount: discountCode.amount };
  });

  private getConfigProperty<T>(obj: Record<string, unknown>, key: string, defaultVal: T): T {
    if (typeof obj[key] === 'boolean') {
      return obj[key] as T;
    }

    return defaultVal;
  }
}
